import {
  Box,
  Button,
  Container,
  Divider,
  TextField,
  Typography,
  InputAdornment,
  Paper,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import { login } from "../features/authorization/authService";
import MicrosoftLoginButton from "../components/microsoftLoginButton/MicrosoftLoginButton";

import { motion, AnimatePresence } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { UserLoginRequest } from "../features/authentication/UserLoginRequest";
import {
  loginUserAsync,
  registerUserAsync,
} from "../features/authentication/authenticationApi";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { HttpStatusCode } from "axios";
import { ROUTES } from "../routes/routes";
import { StringExtension } from "../features/extensions/StringExtension";

const LoginPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authStatus = useAppSelector((state) => state.authentication.status);
  const stringExtension = new StringExtension();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [repeatPassword, setRepeatPassword] = useState<string>("");
  const [repeatPasswordError, setRepeatPasswordError] = useState<string | null>(
    null
  );
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState<boolean>(false);
  const [isHeightLessThan864, setIsHeightLessThan864] =
    useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(authStatus === "loading");
  }, [authStatus]);

  useEffect(() => {
    const handleResize = () => {
      setIsHeightLessThan864(window.innerHeight < 864);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflowY = "auto";

    return () => {
      document.body.style.overflowY = "";
    };
  }, []);

  const validatePassword = (password: string): boolean => {
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNonAlphanumeric = /[^a-zA-Z0-9]/.test(password);
    return hasLowerCase && hasUpperCase && hasNonAlphanumeric;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLoginAsync = async (
    provider: "Google" | "Microsoft" | "Facebook"
  ) => {
    setLoading(true);
    setError(null);

    try {
      await login(provider);
      setTimeout(() => setLoading(false), 1000);
    } catch (error) {
      setLoading(false);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(String(error));
      }
    }
  };

  const handleEmailLogin = async () => {
    setError(null);
    setEmailError(null);
    setPasswordError(null);

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (stringExtension.isEmpty(email)) {
      setEmailError("Please enter your email address");
      return;
    }

    if (stringExtension.isEmpty(password)) {
      setPasswordError("Please enter your password");
      return;
    }

    setLoading(true);

    try {
      const userLoginRequest: UserLoginRequest = {
        email: email,
        password: password,
      };

      const resultAction = await dispatch(loginUserAsync(userLoginRequest));

      if (loginUserAsync.fulfilled.match(resultAction)) {
        navigate("/");
      } else if (loginUserAsync.rejected.match(resultAction)) {
        const errorResponse = resultAction.payload;
        if (errorResponse) {
          switch (errorResponse.status) {
            case HttpStatusCode.BadRequest:
              setError("Invalid request. Please check your input.");
              break;
            case HttpStatusCode.Unauthorized:
              setError("Invalid email or password.");
              break;
            case HttpStatusCode.InternalServerError:
              setError("Server error. Please try again later.");
              break;
            case HttpStatusCode.Forbidden:
              setError(
                "Account is not verified. An email has been sent to confirm your account."
              );
              break;
            default:
              setError("An unknown error occurred.");
              break;
          }
        } else {
          setError("An unknown error occurred.");
        }
      }
    } catch (error) {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    setEmailError(null);
    setPasswordError(null);
    setRepeatPasswordError(null);

    let hasError = false;

    if (stringExtension.isEmpty(firstName)) {
      hasError = true;
    }

    if (stringExtension.isEmpty(lastName)) {
      hasError = true;
    }

    if (stringExtension.isEmpty(email)) {
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      hasError = true;
    }

    if (stringExtension.isEmpty(password)) {
      hasError = true;
    } else if (!validatePassword(password)) {
      setPasswordError("Password must contain at least one lowercase letter, one uppercase letter, and one non-alphanumeric character");
      hasError = true;
    }

    if (stringExtension.isEmpty(repeatPassword)) {
      hasError = true;
    } else if (repeatPassword !== password) {
      setRepeatPasswordError("Passwords don't match");
      hasError = true;
    }

    if (hasError) {
      return;
    }

    try {
      const resultAction = await dispatch(
        registerUserAsync({
          firstName,
          lastName,
          email,
          password,
        })
      );

      if (registerUserAsync.rejected.match(resultAction)) {
        const errorResponse = resultAction.payload;
        if (errorResponse) {
          switch (errorResponse.status) {
            case HttpStatusCode.BadRequest:
              setError("Invalid request. Please check your input.");
              break;
            case HttpStatusCode.Unauthorized:
              setError("Invalid email or password.");
              break;
            case HttpStatusCode.InternalServerError:
              setError("Server error. Please try again later.");
              break;
            case HttpStatusCode.Conflict:
              setError("Email already in use.");
              break;
            case HttpStatusCode.Forbidden:
              setError(
                "Account is not verified. An email has been sent to confirm your account."
              );
              break;
            default:
              setError("An unknown error occurred.");
              break;
          }
        } else {
          setError("An unknown error occurred.");
        }
      } else {
        setIsRegistering(false);
        setError(null);
        navigate(ROUTES.EMAIL_CONFIRMATION, { state: { email } });
      }
    } catch (error) {
      setError("Registration failed.");
    }
  };

  const clearInputFields = () => {
    setEmail("");
    setPassword("");
    setRepeatPassword("");
    setFirstName("");
    setLastName("");
    setRepeatPasswordError(null);
    setError(null);
    setEmailError(null);
    setPasswordError(null);
  };

  useEffect(() => {
    const headerHeight = document.querySelector("header")?.clientHeight || 0;
    document.documentElement.style.setProperty(
      "--header-height",
      `${headerHeight}px`
    );
  }, []);

  const isRegisterFormValid = () => {
    return (
      !stringExtension.isEmpty(firstName) &&
      !stringExtension.isEmpty(lastName) &&
      !stringExtension.isEmpty(email) &&
      !stringExtension.isEmpty(password) &&
      !stringExtension.isEmpty(repeatPassword) &&
      !emailError &&
      !passwordError &&
      !repeatPasswordError
    );
  };

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        height: isHeightLessThan864
          ? "unset"
          : `calc(100vh - var(--header-height, 72px))`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        paddingY: isHeightLessThan864 ? 4 : 0,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          p: { xs: 3, sm: 5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          width: "100%",
        }}
      >
        <svg width="48" height="48" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
          <path d="M32 320 L144 320 L192 384 L280 128 L328 384 L380 320 L480 320" stroke="#0e86d4" strokeWidth="28" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <line x1="280" y1="36" x2="280" y2="116" stroke="#0e86d4" strokeWidth="12" strokeLinecap="round"/>
          <line x1="240" y1="76" x2="320" y2="76" stroke="#0e86d4" strokeWidth="12" strokeLinecap="round"/>
          <line x1="248" y1="44" x2="312" y2="108" stroke="#0e86d4" strokeWidth="12" strokeLinecap="round"/>
          <line x1="312" y1="44" x2="248" y2="108" stroke="#0e86d4" strokeWidth="12" strokeLinecap="round"/>
        </svg>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Welcome to TeamRay
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          {isRegistering
            ? "Create your account to get started"
            : "Sign in with your account to get started"}
        </Typography>

        <AnimatePresence mode="wait">
          {isRegistering ? (
            <motion.div
              key="register"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                margin="normal"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                sx={{ my: 1 }}
                error={!!error && stringExtension.isEmpty(firstName)}
                helperText={!!error && stringExtension.isEmpty(firstName) ? "First name is required" : ""}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                margin="normal"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                sx={{ my: 1 }}
                error={!!error && stringExtension.isEmpty(lastName)}
                helperText={!!error && stringExtension.isEmpty(lastName) ? "Last name is required" : ""}
              />
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                disabled={loading}
                sx={{ my: 1 }}
                error={(!!error && stringExtension.isEmpty(email)) || !!emailError}
                helperText={emailError || (!!error && stringExtension.isEmpty(email) ? "Email is required" : "")}
              />
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setRepeatPasswordError(null);
                  if (passwordError) setPasswordError(null);
                }}
                disabled={loading}
                sx={{ mt: 1, mb: 2 }}
                error={(!!error && stringExtension.isEmpty(password)) || !!passwordError}
                helperText={passwordError || (!!error && stringExtension.isEmpty(password) ? "Password is required" : "")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ minWidth: 0, p: 1 }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Repeat Password"
                type={showRepeatPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                margin="normal"
                value={repeatPassword}
                onChange={(e) => {
                  setRepeatPassword(e.target.value);
                  if (e.target.value !== password) {
                    setRepeatPasswordError("Passwords don't match");
                  } else {
                    setRepeatPasswordError(null);
                  }
                }}
                disabled={loading}
                sx={{ mt: 1, mb: 2 }}
                error={(!!error && stringExtension.isEmpty(repeatPassword)) || !!repeatPasswordError}
                helperText={repeatPasswordError || (!!error && stringExtension.isEmpty(repeatPassword) ? "Repeat password is required" : "")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() =>
                          setShowRepeatPassword(!showRepeatPassword)
                        }
                        sx={{ minWidth: 0, p: 1 }}
                      >
                        {showRepeatPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleRegister}
                  disabled={loading || !isRegisterFormValid()}
                  sx={{ py: 1.5 }}
                >
                  Register
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={() => {
                    setIsRegistering(false);
                    clearInputFields();
                  }}
                  disabled={loading}
                  sx={{ py: 1.5 }}
                >
                  Cancel
                </Button>
              </Box>
              {error && isRegistering && (
                <Alert severity="error" sx={{ mt: 2, textAlign: "left" }}>
                  {error}
                </Alert>
              )}
            </motion.div>
          ) : (
            <>
              <motion.div
                key="login-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                style={{ width: "100%" }}
              >
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={email || ""}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  disabled={loading}
                  sx={{ my: 1 }}
                  error={!!emailError}
                  helperText={emailError}
                />
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setRepeatPasswordError(null);
                    if (passwordError) setPasswordError(null);
                  }}
                  disabled={loading}
                  sx={{ mt: 1, mb: 2 }}
                  error={!!passwordError}
                  helperText={passwordError}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          onClick={() => setShowPassword(!showPassword)}
                          sx={{ minWidth: 0, p: 1 }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ width: "100%", textAlign: "right", mb: 1 }}>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      cursor: "pointer",
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                  >
                    Forgot password?
                  </Typography>
                </Box>
                {error && (
                  <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                    {error}
                  </Alert>
                )}
                <Box sx={{ width: "100%" }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleEmailLogin}
                    disabled={loading}
                    sx={{ py: 1.5, height: 50 }}
                  >
                    Sign in
                  </Button>
                </Box>
              </motion.div>

              <motion.div
                key="external-providers"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                style={{ width: "100%" }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    my: 3,
                  }}
                >
                  <Divider sx={{ flexGrow: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      mx: 2,
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      letterSpacing: "0.05em",
                    }}
                  >
                    OR CONTINUE WITH
                  </Typography>
                  <Divider sx={{ flexGrow: 1 }} />
                </Box>
                <Box
                  sx={{
                    pointerEvents: loading ? "none" : "auto",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <Button
                    fullWidth
                    startIcon={
                      <svg viewBox="0 0 24 24" width="20" height="20">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                    }
                    onClick={() => handleLoginAsync("Google")}
                    disabled={loading}
                    sx={{
                      height: 50,
                      borderRadius: "12px",
                      border: "1px solid rgba(14, 134, 212, 0.15)",
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(8px)",
                      color: "#1a2332",
                      fontSize: "15px",
                      fontWeight: 500,
                      textTransform: "none",
                      opacity: loading ? 0.6 : 1,
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        borderColor: "rgba(14, 134, 212, 0.3)",
                        boxShadow: "0 2px 8px rgba(14, 134, 212, 0.1)",
                        transform: "translateY(-1px)",
                      },
                      "&:active": {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        boxShadow: "none",
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    Sign in with Google
                  </Button>

                  <MicrosoftLoginButton
                    onClick={() => handleLoginAsync("Microsoft")}
                    isDisabled={loading}
                  />

                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Don't have an account?{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          fontWeight: 600,
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                        onClick={() => {
                          setIsRegistering(true);
                          clearInputFields();
                        }}
                      >
                        Sign Up
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </Paper>
    </Container>
  );
};

export default LoginPage;
